import { getAllAncestorIds } from '../../../../../common/dataStructures/treeUtils';
import { isLocationTrash } from '../../../../../common/elements/utils/elementLocationUtils';
import { getElementId, getRootBoardProperty } from '../../../../../common/elements/utils/elementPropertyUtils';
import { isBoard } from '../../../../../common/elements/utils/elementTypeUtils';
import { prop, propIn } from '../../../../../common/utils/immutableHelper';
import { getUrlPermissionId } from '../../../../app/routingSelector';
import { parentIdMapSelector } from '../../../../element/selectors/elementGraphSelector';
import { getElements } from '../../../../element/selectors/elementsSelector';
import { getCurrentBoardIdFromState } from '../../../../reducers/currentBoardId/currentBoardIdSelector';
import { Selector } from '../../../../types/reduxTypes';
import { getSortedViewableViewedBoardIds } from '../../../../user/navigationHistory/navigationHistorySelector';
import getGridSize from '../../../../utils/grid/gridSizeSelector';
import { createShallowSelector } from '../../../../utils/milanoteReselect/milanoteReselect';
import { getBoardIdToPermissionIdMapSelector } from '../../../../utils/permissions/permissionsSelector';
import { BoardLinkElement, BoardLinkSearchItem } from './boardLinkTypes';
import { mapBoardToBoardLinkItem } from './boardLinkUtils';

type BoardLinkSelectorInputs = {
    gridSize: number;
    elements: ReturnType<typeof getElements>;
    currentBoardId?: string;
    viewedBoardIds: string[];
    boardIdPermissionIdMap: ReturnType<typeof getBoardIdToPermissionIdMapSelector>;
    urlPermissionId?: string;
    parentIdMap: ReturnType<typeof parentIdMapSelector>;
};

export type BoardLinkLists = {
    gridSize: number;
    recentViewedItems: BoardLinkSearchItem[];
    allLoadedBoardItems: BoardLinkSearchItem[];
};

export const SEARCH_SIZE = 20;

const shouldFilterBoardFromResults = (board?: BoardLinkElement): boolean => {
    if (!board) return true;
    if (isLocationTrash(board)) return true;
    if (getRootBoardProperty(board)) return true;
    if (propIn(['meta', 'safeToDelete'], board)) return true;

    return false;
};

const getClosestPermissionIdForBoard = ({
    boardId,
    currentBoardId,
    urlPermissionId,
    boardIdPermissionIdMap,
    parentIdMap,
}: {
    boardId: string;
    currentBoardId?: string;
    urlPermissionId?: string;
    boardIdPermissionIdMap: ReturnType<typeof getBoardIdToPermissionIdMapSelector>;
    parentIdMap: ReturnType<typeof parentIdMapSelector>;
}): string | undefined => {
    if (urlPermissionId && currentBoardId === boardId) return;

    const directPermissionId = boardIdPermissionIdMap?.get(boardId);
    if (directPermissionId) return directPermissionId;

    const ancestorIds = getAllAncestorIds(parentIdMap, boardId);

    if (urlPermissionId && ancestorIds?.includes(currentBoardId || '')) {
        return urlPermissionId;
    }

    for (const ancestorId of ancestorIds) {
        const permissionId = boardIdPermissionIdMap?.get(ancestorId);

        if (permissionId) return permissionId;
    }

    return undefined;
};

const toBoardLinkItem = (
    board: BoardLinkElement,
    urlPermissionId: string | undefined,
    boardIdPermissionIdMap: ReturnType<typeof getBoardIdToPermissionIdMapSelector>,
    parentIdMap: ReturnType<typeof parentIdMapSelector>,
    currentBoardId?: string,
): BoardLinkSearchItem => {
    const boardId = getElementId(board) as string;
    const permissionId = getClosestPermissionIdForBoard({
        boardId,
        currentBoardId,
        urlPermissionId,
        boardIdPermissionIdMap,
        parentIdMap,
    });

    return mapBoardToBoardLinkItem(board, permissionId);
};

const isSearchableBoard = (board: BoardLinkElement, currentBoardId?: string): boolean => {
    if (getElementId(board) === currentBoardId) return false;

    return !shouldFilterBoardFromResults(board);
};

const selectBoardLinkSelectorInputs: Selector<BoardLinkSelectorInputs> = (state) => ({
    gridSize: getGridSize(state),
    elements: getElements(state),
    currentBoardId: getCurrentBoardIdFromState(state),
    viewedBoardIds: getSortedViewableViewedBoardIds(state),
    boardIdPermissionIdMap: getBoardIdToPermissionIdMapSelector(state),
    urlPermissionId: getUrlPermissionId(state),
    parentIdMap: parentIdMapSelector(state),
});

export const makeSelectBoardLinkLists = () =>
    createShallowSelector(selectBoardLinkSelectorInputs, (inputs): BoardLinkLists => {
        const {
            gridSize,
            elements,
            currentBoardId,
            viewedBoardIds,
            boardIdPermissionIdMap,
            urlPermissionId,
            parentIdMap,
        } = inputs;
        const recentViewedItems = viewedBoardIds
            .map((boardId) => prop(boardId, elements) as BoardLinkElement | undefined)
            .filter((board): board is BoardLinkElement => !!board && isSearchableBoard(board, currentBoardId))
            .map((board) =>
                toBoardLinkItem(board, urlPermissionId, boardIdPermissionIdMap, parentIdMap, currentBoardId),
            )
            .slice(0, SEARCH_SIZE);

        const viewedBoardPositionMap = viewedBoardIds.reduce<Record<string, number>>((acc, boardId, index) => {
            acc[boardId] = index;
            return acc;
        }, {});

        const loadedBoards: BoardLinkElement[] = elements.reduce((boards: BoardLinkElement[], element) => {
            if (!isBoard(element)) return boards;

            boards.push(element as BoardLinkElement);
            return boards;
        }, []);

        const allLoadedBoardItems = loadedBoards
            .filter((element) => isBoard(element))
            .filter((board) => isSearchableBoard(board, currentBoardId))
            .map((board) =>
                toBoardLinkItem(board, urlPermissionId, boardIdPermissionIdMap, parentIdMap, currentBoardId),
            )
            .map((item) => {
                return {
                    item: item,
                    viewedBoardPosition: viewedBoardPositionMap[item.boardId] ?? Number.MAX_SAFE_INTEGER,
                };
            })
            .sort((itemA, itemB) => {
                if (itemA.viewedBoardPosition !== itemB.viewedBoardPosition) {
                    return itemA.viewedBoardPosition - itemB.viewedBoardPosition;
                }

                return itemA.item.title.localeCompare(itemB.item.title);
            })
            .map(({ item }) => item);

        return {
            gridSize,
            recentViewedItems: recentViewedItems || [],
            allLoadedBoardItems,
        };
    });
